import { RiskBandsRiskValue } from "@/store/services/facades/types";
import { riskValueThresholds } from "../constants/thresholds";
import { AbcdeRiskValue, WordyRiskValue } from "../schema";

/**
 * Converts a given MVAR to an ABCDE risk value. Does NOT care if it should
 * limit D or E because user is not on HKMA, but instead supports a maxLetter
 * property to cap the grading. Seems overengineered, but it keeps this rather
 * core function very modular and testable.
 * @param mvar - number
 * @param maxLetter - AbcdeRiskValue
 * @returns A letter between A-E.
 */
export const mvarToGradedRiskValue = (mvar: number, maxLetter?: AbcdeRiskValue): AbcdeRiskValue => {
  if (mvar < riskValueThresholds.A) {
    throw new Error("Risk value too low. This must be investigated. Please report this issue.");
  }
  if (mvar < riskValueThresholds.B || maxLetter === "A") {
    return "A";
  }
  if (mvar < riskValueThresholds.C || maxLetter === "B") {
    return "B";
  }
  if (mvar < riskValueThresholds.D || maxLetter === "C") {
    return "C";
  }
  if (mvar < riskValueThresholds.E || maxLetter === "D") {
    return "D";
  }
  return "E";
};

/**
 * Converts the given risk value to ABCDE, and does nothing if already ABC.
 * @param band - RiskBandsRiskValue
 * @returns A letter between A-E.
 */
export const ensureGradedRiskValue = (band: RiskBandsRiskValue | AbcdeRiskValue): AbcdeRiskValue => {
  switch (band) {
    // Short-circuit if an AbcdeRiskValue.
    case "A":
    case "B":
    case "C":
    case "D":
    case "E":
      return band;
    case "LowExpectedPremium":
      return "A";
    case "ModerateExpectedPremium":
      return "B";
    case "Uninsurable":
      return "C";
    case "UninsurableHighConcern":
      return "D";
    case "UninsurableExtremeConcern":
      return "E";
  }
};

/**
 * Converts the given risk value to Wordy, and does nothing if already so.
 * @param band - RiskBandsRiskValue
 * @returns One of five long risk value texts.
 */
export const ensureWordyRiskValue = (band: RiskBandsRiskValue | AbcdeRiskValue): WordyRiskValue => {
  switch (band) {
    // Short-circuit if a wordy value.
    case "LowExpectedPremium":
    case "ModerateExpectedPremium":
    case "Uninsurable":
    case "UninsurableHighConcern":
    case "UninsurableExtremeConcern":
      return band;
    case "A":
      return "LowExpectedPremium";
    case "B":
      return "ModerateExpectedPremium";
    case "C":
      return "Uninsurable";
    case "D":
      return "UninsurableHighConcern";
    case "E":
      return "UninsurableExtremeConcern";
  }
};
